import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import { userWorkExperience, userDocsUpload, userIncomeSources } from "../../actions/user";
import { useLocation } from "react-router-dom";
import { refetchWorkUpdate } from "../UserSettings/WorkUpdate";

const EMPLOYMENT_TYPES = [
  { title: "Full-time", value: "full_time" },
  { title: "Part-time", value: "part_time" },
  { title: "Freelance / Contract", value: "freelance_contract" },
  { title: "Self employed", value: "self_employed" },
  { title: "Student", value: "student" },
];

const SALARY_FREQUENCIES = [
  { title: "Monthly", value: "monthly" },
  { title: "Weekly", value: "weekly" },
  { title: "Yearly", value: "yearly" },
];

const SOURCE_OF_INCOME = [
  { title: "Salary", value: "salary" },
  { title: "Family", value: "family" },
  { title: "Employment", value: "employment" },
  { title: "Freelance", value: "freelance" },
  { title: "Investment", value: "investment" },
  { title: "Business", value: "business" },
  { title: "Rental", value: "rental" },
  { title: "Pension", value: "pension" },
  { title: "Other", value: "other" },
];

const DOCUMENT_TYPES = [
  { title: "Proof of Funds", value: "proof_of_funds" },
  { title: "Proof of Work", value: "proof_of_work" },
  { title: "Bank Statement", value: "bank_statement" },
  { title: "Payslip", value: "payslip" },
  { title: "Tax Document", value: "tax_document" },
  { title: "Business Registration", value: "business_registration" },
  { title: "Utility Bill", value: "utility_bill" },
  { title: "Other", value: "other" },
];

const initialWorkForm = {
  company_name: "",
  employment_type: "",
  industry: "",
  job_title: "",
  job_description: "",
  start_date: "",
  end_date: "",
  salary: "",
  salary_frequency: "",
  currency: "",
  location: "",
  can_contact_employer: false,
  source_of_income: "",
};

const createEmptyDocRow = () => ({
  documentType: "",
  description: "",
  file: null,
  isUploading: false,
  isUploaded: false,
  serverMeta: null,
  errorText: "",
  inputKey: Math.random().toString(36).slice(2),
});

// Remove end_date when current = true, keep it otherwise
const stripEndDateIfCurrent = (data, isCurrent) => {
  const { end_date, ...rest } = data;        // pull end_date out
  return isCurrent ? rest : { ...rest, end_date };
};

// Build only the date fields you need (used for income payload too)
const buildDateFields = (start, end, isCurrent) =>
  isCurrent ? { start_date: start } : { start_date: start, end_date: end };



const WorkUpdate = () => {
  const location = useLocation();
  const [workForm, setWorkForm] = useState(initialWorkForm);
  const [formInstanceKey, setFormInstanceKey] = useState(0);

  const [isCurrentEmployment, setIsCurrentEmployment] = useState(false);

  const [workTouched, setWorkTouched] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const closeButtonRef = useRef(null);
  const isSettingsPage = location.pathname.includes('user/settings');

  const [documentRows, setDocumentRows] = useState([createEmptyDocRow()]);

  // ----------------- helpers -----------------
  const updateWorkField = (fieldName, value) => {
    setWorkTouched((prev) => ({ ...prev, [fieldName]: true }));
    setWorkForm((prev) => ({ ...prev, [fieldName]: value }));
  };

  const removeDocumentRow = (rowIndex) => {
    setDocumentRows((prev) => {
      if (prev.length === 1) return [createEmptyDocRow()];
      const next = [...prev];
      next.splice(rowIndex, 1);
      return next.length ? next : [createEmptyDocRow()];
    });
  };


  const validateWorkField = (fieldName) => {
    // end_date is only required when NOT current
    if (fieldName === "end_date") {
      if (!isCurrentEmployment && !String(workForm.end_date || "").trim()) {
        return "This field is required.";
      }
      return "";
    }

    if (fieldName === "can_contact_employer") {
      // must be boolean
      return typeof workForm.can_contact_employer !== "boolean"
        ? "Please select Yes or No."
        : "";
    }

    const value = String(workForm[fieldName] ?? "").trim();
    const requiredFields = [
      "company_name",
      "employment_type",
      "industry",
      "job_title",
      "job_description",
      "start_date",
      "salary",
      "salary_frequency",
      "currency",
      "location",
      "source_of_income",
    ];

    if (requiredFields.includes(fieldName) && !value) {
      return "This field is required.";
    }
    if (fieldName === "salary") {
      const numeric = Number(workForm.salary);
      if (!numeric || numeric <= 0) return "Enter a valid amount.";
    }
    return "";
  };

  const isWorkFormValid = () => {
    const baseFields = [
      "company_name",
      "employment_type",
      "industry",
      "job_title",
      "job_description",
      "start_date",
      "salary",
      "salary_frequency",
      "currency",
      "location",
      "source_of_income",
    ];
    const baseFilled = baseFields.every((field) => String(workForm[field]).trim());
    const salaryOk = Number(workForm.salary) > 0;
    const canContactOk = typeof workForm.can_contact_employer === "boolean";
    const endDateOk = isCurrentEmployment
      ? true
      : String(workForm.end_date || "").trim().length > 0;

    return baseFilled && salaryOk && canContactOk && endDateOk;
  };

  const updateDocRowField = (rowIndex, key, value) => {
    setDocumentRows((prev) => {
      const next = [...prev];
      next[rowIndex] = { ...next[rowIndex], [key]: value, errorText: "" };
      return next;
    });
  };

  const isDocRowComplete = (row) =>
    row.documentType.trim() && row.description.trim() && row.file instanceof File;

  const uploadSingleDocument = async (rowIndex) => {
    const row = documentRows[rowIndex];
    if (!isDocRowComplete(row)) {
      setDocumentRows((prev) => {
        const next = [...prev];
        next[rowIndex] = {
          ...next[rowIndex],
          errorText: "Please select document type, add description, and choose a file.",
        };
        return next;
      });
      return false;
    }

    try {
      updateDocRowField(rowIndex, "isUploading", true);

      const formData = new FormData();
      formData.append("document_type", row.documentType);
      formData.append("description", row.description);
      formData.append("file", row.file);

      const response = await userDocsUpload(formData);
      // console.log(response);
      await refetchWorkUpdate();
      
      const docMeta = response?.data?.document || response?.data || null;

      setDocumentRows((prev) => {
        const next = [...prev];
        next[rowIndex] = {
          ...next[rowIndex],
          isUploading: false,
          isUploaded: true,
          serverMeta: docMeta,
          errorText: "",
          inputKey: Math.random().toString(36).slice(2),
        };
        // Add a new empty row beneath the uploaded one
        next.splice(rowIndex + 1, 0, createEmptyDocRow());
        return next;
      });

      toast.success("Document uploaded successfully.");
      return true;
    } catch (error) {
      setDocumentRows((prev) => {
        const next = [...prev];
        next[rowIndex] = {
          ...next[rowIndex],
          isUploading: false,
          errorText:
            error?.response?.data?.message ||
            "Failed to upload document. Please try again.",
        };
        return next;
      });
      return false;
    }
  };

  const handleUploadAndAddMore = async (rowIndex) => {
    await uploadSingleDocument(rowIndex);
  };

  const resetAll = () => {
    setWorkForm(initialWorkForm);
    setIsCurrentEmployment(false);
    setWorkTouched({});
    setDocumentRows([createEmptyDocRow()]);
    setFormInstanceKey((k) => k + 1);
  };


  // ----------------- submit -----------------
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!isWorkFormValid()) {
      setWorkTouched({
        company_name: true,
        employment_type: true,
        industry: true,
        job_title: true,
        job_description: true,
        start_date: true,
        end_date: true,
        salary: true,
        salary_frequency: true,
        currency: true,
        location: true,
        can_contact_employer: true,
        source_of_income: true,
      });
      toast.error("Please complete all work fields correctly.");
      return;
    }

    const hasUploadedDoc = documentRows.some((row) => row.isUploaded);
    const hasCompletableRow = documentRows.some((row) => isDocRowComplete(row));
    if (!hasUploadedDoc && !hasCompletableRow) {
      toast.error("Please add at least one supporting document.");
      return;
    }

    setSubmitLoading(true);
    try {
      // upload any complete-but-not-uploaded rows
      for (let rowIndex = 0; rowIndex < documentRows.length; rowIndex += 1) {
        const row = documentRows[rowIndex];
        if (!row.isUploaded && isDocRowComplete(row)) {
          // const uploaded = await uploadSingleDocument(rowIndex);
          // if (!uploaded) throw new Error("Document upload failed during submission.");
        }
      }

      const payload = {
        ...stripEndDateIfCurrent(workForm, isCurrentEmployment),
        is_current: isCurrentEmployment,
      };

      const payloadIncome = {
        source_type: workForm.source_of_income,
        employer_or_business_name: workForm.company_name,
        description: workForm.job_description,
        amount: workForm.salary,
        currency: workForm.currency,
        frequency: workForm.salary_frequency,
        ...buildDateFields(workForm.start_date, workForm.end_date, isCurrentEmployment),
        is_current: isCurrentEmployment,
      };

      const result = await userWorkExperience(payload);
      const resultIncome = await userIncomeSources(payloadIncome);
      // console.log(result);
      // console.log(resultIncome);
      toast.success("Work information saved successfully.");
      resetAll();
      setTimeout(() => {
        closeButtonRef.current?.click();
      }, 600);
      await refetchWorkUpdate();
    } catch (error) {
      // console.error(error);
      const fallback =
      error?.response?.data?.errors?.message ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to submit. Please try again.";
      toast.error(fallback);
      // console.log(error);
      
    } finally {
      setSubmitLoading(false);
    }
  };

  // ----------------- render -----------------
  return (
    <section
      className="modal fade user-actions-modal"
      id="workUpdateModal"
      tabIndex="-1"
      aria-labelledby="workUpdateModalLabel"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content pb-3">
          <div className="d-flex justify-content-end p-3">
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              ref={closeButtonRef}
            />
          </div>

          <div className="modal-body px-4 px-md-5">
            <h4 className="mb-3">Work Information</h4>

            <form key={formInstanceKey} onSubmit={handleSubmit} className="d-grid gap-4 card p-1">
              <div className="">
                <div className="card-body">
                  <h5 className="mb-3">Work Details</h5>

                  <div className="row g-3">
                    {/* company & employment */}
                    <div className="col-md-6">
                      <label className="form-label">Company Name</label>
                      <input
                        className="form-control"
                        value={workForm.company_name}
                        onChange={(e) => updateWorkField("company_name", e.target.value)}
                      />
                      {workTouched.company_name && validateWorkField("company_name") && (
                        <small className="text-danger">{validateWorkField("company_name")}</small>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">Employment Type</label>
                      <select
                        className="form-select"
                        value={workForm.employment_type}
                        onChange={(e) => updateWorkField("employment_type", e.target.value)}
                      >
                        <option value="">Select…</option>
                        {EMPLOYMENT_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>{type.title}</option>
                        ))}
                      </select>
                      {workTouched.employment_type && validateWorkField("employment_type") && (
                        <small className="text-danger">{validateWorkField("employment_type")}</small>
                      )}
                    </div>

                    {/* industry & job title */}
                    <div className="col-md-6">
                      <label className="form-label">Industry</label>
                      <input
                        className="form-control"
                        value={workForm.industry}
                        onChange={(e) => updateWorkField("industry", e.target.value)}
                      />
                      {workTouched.industry && validateWorkField("industry") && (
                        <small className="text-danger">{validateWorkField("industry")}</small>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">Job Title</label>
                      <input
                        className="form-control"
                        value={workForm.job_title}
                        onChange={(e) => updateWorkField("job_title", e.target.value)}
                      />
                      {workTouched.job_title && validateWorkField("job_title") && (
                        <small className="text-danger">{validateWorkField("job_title")}</small>
                      )}
                    </div>

                    {/* description */}
                    <div className="col-12">
                      <label className="form-label">Job Description</label>
                      <textarea
                        className="form-control"
                        rows={3}
                        value={workForm.job_description}
                        onChange={(e) => updateWorkField("job_description", e.target.value)}
                      />
                      {workTouched.job_description && validateWorkField("job_description") && (
                        <small className="text-danger">{validateWorkField("job_description")}</small>
                      )}
                    </div>

                    {/* dates */}
                    <div className="col-md-4">
                      <label className="form-label">Start Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={workForm.start_date}
                        onChange={(e) => updateWorkField("start_date", e.target.value)}
                      />
                      {workTouched.start_date && validateWorkField("start_date") && (
                        <small className="text-danger">{validateWorkField("start_date")}</small>
                      )}
                    </div>

                    <div className="col-md-4 d-flex flex-column align-items-center">
                      <label htmlFor="currentEmploymentSwitch" className="form-label">Still Working here?</label>
                      <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="currentEmploymentSwitch"
                            checked={isCurrentEmployment}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setIsCurrentEmployment(checked);
                              // When current is true, clear any end_date in the form
                              if (checked) updateWorkField("end_date", "");
                            }}
                          />
                          <label className="form-check-label" htmlFor="currentEmploymentSwitch">
                            {isCurrentEmployment ? "Yes" : "No"}
                          </label>
                        </div>
                    </div>

                    {!isCurrentEmployment && (
                      <div className="col-md-4">
                        <label className="form-label">End Date</label>
                        <input
                          type="date"
                          className="form-control"
                          value={workForm.end_date}
                          onChange={(e) => updateWorkField("end_date", e.target.value)}
                        />
                        {workTouched.end_date && validateWorkField("end_date") && (
                          <small className="text-danger">{validateWorkField("end_date")}</small>
                        )}
                      </div>
                    )}

                    {/* salary & frequency */}
                    <div className="col-md-4">
                      <label className="form-label">Salary</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="form-control"
                        value={workForm.salary}
                        onChange={(e) => updateWorkField("salary", e.target.value)}
                      />
                      {workTouched.salary && validateWorkField("salary") && (
                        <small className="text-danger">{validateWorkField("salary")}</small>
                      )}
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">Salary Frequency</label>
                      <select
                        className="form-select"
                        value={workForm.salary_frequency}
                        onChange={(e) => updateWorkField("salary_frequency", e.target.value)}
                      >
                        <option value="">Select…</option>
                        {SALARY_FREQUENCIES.map((type) => (
                          <option key={type.value} value={type.value}>{type.title}</option>
                        ))}
                      </select>
                      {workTouched.salary_frequency && validateWorkField("salary_frequency") && (
                        <small className="text-danger">{validateWorkField("salary_frequency")}</small>
                      )}
                    </div>

                    {/* currency & location */}
                    <div className="col-md-4">
                      <label className="form-label" title="Payment received in">Currency</label>
                      <input
                        className="form-control"
                        placeholder="Use code (e.g. USD, EUR, NGN, GHS, MKW)"
                        value={workForm.currency}
                        onChange={(e) => updateWorkField("currency", e.target.value)}
                      />
                      {workTouched.currency && validateWorkField("currency") && (
                        <small className="text-danger">{validateWorkField("currency")}</small>
                      )}
                    </div>

                    <div className="col-md-12">
                      <label className="form-label">Location (Full address)</label>
                      <input
                        className="form-control"
                        placeholder="Street, City, State, Country"
                        value={workForm.location}
                        onChange={(e) => updateWorkField("location", e.target.value)}
                      />
                      {workTouched.location && validateWorkField("location") && (
                        <small className="text-danger">{validateWorkField("location")}</small>
                      )}
                    </div>

                    {/* employer contact & source of income */}
                    <div className="col-md-6">
                      <label className="form-label">Can we contact your employer?</label>
                      <select
                        className="form-select"
                        value={workForm.can_contact_employer ? "true" : "false"} 
                        onChange={(e) => updateWorkField("can_contact_employer", e.target.value === "true")}
                      >
                        <option value="false">No</option>
                        <option value="true">Yes</option>
                      </select>
                      {workTouched.can_contact_employer && validateWorkField("can_contact_employer") && (
                        <small className="text-danger">{validateWorkField("can_contact_employer")}</small>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">Source of Income</label>
                      <select
                        className="form-select"
                        value={workForm.source_of_income}
                        onChange={(e) => updateWorkField("source_of_income", e.target.value)}
                      >
                        <option value="">Select…</option>
                        {SOURCE_OF_INCOME.map((option) => (
                          <option key={option.value} value={option.value}>{option.title}</option>
                        ))}
                      </select>
                      {workTouched.source_of_income && validateWorkField("source_of_income") && (
                        <small className="text-danger">
                          {validateWorkField("source_of_income")}
                        </small>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Supporting Documents */}
              <div className="">
                <div className="card-body">
                  <h5 className="mb-2">Supporting Documents</h5>

                  {documentRows.map((row, documentIndex) => (
                    <div key={documentIndex} className="border rounded p-3 mb-3">
                      <div className="row g-3 align-items-end">
                        <div className="col-md-4">
                          <label className="form-label">Document Type</label>
                          <select
                            className="form-select"
                            value={row.documentType}
                            onChange={(e) =>
                              updateDocRowField(documentIndex, "documentType", e.target.value)
                            }
                            disabled={row.isUploading || row.isUploaded}
                          >
                            <option value="">Select…</option>
                            {DOCUMENT_TYPES.map((type) => (
                              <option key={type.value} value={type.value}>{type.title}</option>
                            ))}
                          </select>
                        </div>

                        <div className="col-md-5">
                          <label className="form-label">Description</label>
                          <input
                            className="form-control"
                            placeholder="Short description"
                            value={row.description}
                            onChange={(e) =>
                              updateDocRowField(documentIndex, "description", e.target.value)
                            }
                            disabled={row.isUploading || row.isUploaded}
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="form-label">File</label>
                          <input
                            key={row.inputKey} 
                            type="file"
                            className="form-control"
                            onChange={(e) =>
                              updateDocRowField(
                                documentIndex,
                                "file",
                                e.target.files?.[0] || null
                              )
                            }
                            disabled={row.isUploading || row.isUploaded}
                          />
                        </div>
                      </div>

                      <div className="d-flex gap-2 mt-3">
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={() => handleUploadAndAddMore(documentIndex)}
                          disabled={
                            row.isUploading ||
                            row.isUploaded ||
                            !row.documentType ||
                            !row.description ||
                            !(row.file instanceof File)
                          }
                        >
                          {row.isUploading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" />
                              Uploading…
                            </>
                          ) : row.isUploaded ? (
                            "Uploaded ✓"
                          ) : (
                            "Upload & Add more"
                          )}
                        </button>

                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => removeDocumentRow(documentIndex)}
                          disabled={row.isUploading}
                        >
                          Remove
                        </button>


                        {row.isUploaded && (
                          <span className="badge bg-success align-self-center">Ready ✓</span>
                        )}
                      </div>

                      {row.errorText && (
                        <div className="text-danger small mt-2">{row.errorText}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="d-flex justify-content-end">
                <button
                  type="submit"
                  className="btn btn-primary px-4"
                  disabled={submitLoading}
                >
                  {submitLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Saving…
                    </>
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>

              <div className="small text-muted">
                <em>
                  Tip: If you want to upload multiple documents, press <strong>Upload & Add more</strong> for each.
                  Otherwise, attach just one document and press <strong>Submit</strong>.
                </em>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WorkUpdate;